<!--
/*
 * Компонент ProfilePage — страница профиля пользователя
 */
-->
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ERouteNames } from '@/router/ERouteNames'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import { usePayStore } from '@/modules/pay/store/payStore'
import EmailBindDialog from '@/modules/user/components/EmailBindDialog.vue'
import ShareReferralButton from '@/components/share-referral-button/ShareReferralButton.vue'
import ReferralBonusTooltip from '@/components/referral-bonus-tooltip/ReferralBonusTooltip.vue'
import LeaderboardRulesTooltip from '@/components/leaderboard-rules-tooltip/LeaderboardRulesTooltip.vue'
import SavedCardItem from '@/components/profile/SavedCardItem.vue'
import { isTelegram } from '@/utils/isTelegram'
import { useClipboard } from '@vueuse/core'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { useSettingsStore } from '@/modules/settings/store/settingsStore'
import { useApiStore } from '@/modules/api/store/apiStore'

const { mobile } = useDisplay()
const { t, locale } = useI18n()

const userStore = useUserStore()
const authStore = useAuthStore()
const payStore = usePayStore()
const feedbackStore = useFeedbackStore()
const settingsStore = useSettingsStore()
const apiStore = useApiStore()
const router = useRouter()

onMounted(async () => {
  if (!apiStore.tariffs.length) await apiStore.getTariffs()
  await payStore.loadSavedPaymentMethods()
})

const { copy } = useClipboard()

const copyToClipboard = (value: string, label: string) => {
  copy(value)
  feedbackStore.showToast({ type: 'info', message: `${label} ${t('common.copied')}` })
}

const isWebVersion = computed(() => {
  return !isTelegram()
})

const isLoading = ref(false)

const handleTelegramOidcLink = async () => {
  if (!settingsStore.telegramBotId) {
    feedbackStore.showToast({ type: 'error', message: t('errors.telegramBotIdMissing') })
    return
  }

  isLoading.value = true
  try {
    const tgAuthData = await new Promise<any>((resolve, reject) => {
      if (!window.Telegram?.Login) {
        reject(new Error(t('errors.telegramLoginUnavailable')))
        return
      }
      window.Telegram.Login.auth(
        { client_id: String(settingsStore.telegramBotId), request_access: 'write' },
        (data: any) => {
          if (data) {
            resolve(data)
          } else {
            reject(new Error(t('errors.telegramAuthCancelled')))
          }
        }
      )
    })

    const result = await authStore.linkTelegramOIDC({ idToken: tgAuthData.id_token })
    if (result.ok) {
      feedbackStore.showToast({ type: 'success', message: t('common.success') })
    } else {
      feedbackStore.showToast({
        type: 'error',
        message: result.error || t('errors.telegramLinkFailed'),
      })
    }
  } catch (err: any) {
    if (err.message !== t('errors.telegramAuthCancelled')) {
      feedbackStore.showToast({ type: 'error', message: err.message || t('errors.telegramAuthError') })
    }
  } finally {
    isLoading.value = false
  }
}

const showEmailDialog = ref(false)
const showLogoutConfirm = ref(false)
const showEmailUnlinkConfirm = ref(false)
const showTelegramUnlinkConfirm = ref(false)
const showDeleteConfirm = ref(false)
const isUnlinkingEmail = ref(false)
const isUnlinkingTelegram = ref(false)
const isDeletingAccount = ref(false)

const isEditingName = ref(false)
const editNameValue = ref('')
const isSavingName = ref(false)
const isSavingPrivacy = ref(false)

const startEditingName = () => {
  editNameValue.value = userStore.user?.firstName || ''
  isEditingName.value = true
}

const saveName = async () => {
  const trimmed = editNameValue.value.trim()
  if (!trimmed) {
    feedbackStore.showToast({ type: 'warning', message: t('errors.nameRequired') })
    return
  }

  isSavingName.value = true
  try {
    const result = await userStore.updateProfileName(trimmed)
    if (result.ok) {
      feedbackStore.showToast({ type: 'success', message: t('common.success') })
      isEditingName.value = false
    } else {
      feedbackStore.showToast({ type: 'error', message: result.error || t('errors.updateNameFailed') })
    }
  } catch (err: any) {
    feedbackStore.showToast({ type: 'error', message: err.message || t('errors.tryLater') })
  } finally {
    isSavingName.value = false
  }
}

const handlePrivacyChange = async (val: boolean | null) => {
  const isPublic = Boolean(val)
  isSavingPrivacy.value = true
  try {
    const result = await userStore.updateShowNamePublic(isPublic)
    if (result.ok) {
      feedbackStore.showToast({
        type: 'success',
        message: t('common.success'),
      })
    } else {
      feedbackStore.showToast({ type: 'error', message: result.error || t('errors.privacyFailed') })
    }
  } catch (err: any) {
    feedbackStore.showToast({ type: 'error', message: err.message || t('errors.tryLater') })
  } finally {
    isSavingPrivacy.value = false
  }
}

const formattedTariffExpiry = computed(() => {
  if (!userStore.user?.lastTariffExpiresAt) return '—'
  const d = userStore.user.lastTariffExpiresAt
  const loc = locale.value === 'ru' ? 'ru-RU' : 'en-US'
  if (typeof d === 'object' && d !== null && 'seconds' in d) {
    const sec = (d as any).seconds
    const ms = typeof sec === 'bigint' ? Number(sec) * 1000 : Number(sec) * 1000
    return new Date(ms).toLocaleDateString(loc)
  }
  return new Date(d).toLocaleDateString(loc)
})

const goBack = () => {
  router.push({ name: ERouteNames.HOME })
}

const handleLogout = async () => {
  showLogoutConfirm.value = false
  await authStore.logout('Logout')
  await router.push({ name: ERouteNames.AUTH })
}

const handleEmailUnlink = async () => {
  isUnlinkingEmail.value = true
  try {
    const result = await userStore.clearEmail()
    if (result.ok) {
      feedbackStore.showToast({ type: 'success', message: t('common.success') })
      showEmailUnlinkConfirm.value = false
    } else {
      feedbackStore.showToast({ type: 'error', message: result.error || t('errors.unlinkEmailFailed') })
    }
  } catch (err: any) {
    feedbackStore.showToast({ type: 'error', message: err.message || t('errors.tryLater') })
  } finally {
    isUnlinkingEmail.value = false
  }
}

const handleTelegramUnlink = async () => {
  isUnlinkingTelegram.value = true
  try {
    const result = await userStore.clearTelegram()
    if (result.ok) {
      feedbackStore.showToast({ type: 'success', message: t('common.success') })
      showTelegramUnlinkConfirm.value = false
    } else {
      feedbackStore.showToast({ type: 'error', message: result.error || t('errors.unlinkTelegramFailed') })
    }
  } catch (err: any) {
    feedbackStore.showToast({ type: 'error', message: err.message || t('errors.tryLater') })
  } finally {
    isUnlinkingTelegram.value = false
  }
}

const handleDeleteAccount = async () => {
  isDeletingAccount.value = true
  try {
    const result = await userStore.deleteCurrentUser()
    if (result.ok) {
      feedbackStore.showToast({ type: 'success', message: t('common.success') })
      showDeleteConfirm.value = false
      await authStore.logout('Delete')
      await router.push({ name: ERouteNames.AUTH })
    } else {
      feedbackStore.showToast({ type: 'error', message: result.error || t('errors.deleteAccountFailed') })
    }
  } catch (err: any) {
    feedbackStore.showToast({ type: 'error', message: err.message || t('errors.tryLater') })
  } finally {
    isDeletingAccount.value = false
  }
}
</script>

<template>
  <v-container fluid class="d-flex flex-column ga-4" :class="mobile ? 'pa-4' : 'py-4 px-0'">
    <!-- Шапка -->
    <div class="d-flex align-center w-100">
      <v-btn
        variant="text"
        size="small"
        prepend-icon="ri-arrow-left-line"
        @click="goBack"
      >
        {{ t('common.back') }}
      </v-btn>
      <div class="flex-grow-1 text-center">
        <span class="text-h4">{{ t('profile.title') }}</span>
      </div>
    </div>

    <!-- v-alert -->
    <v-alert
      variant="tonal"
      color="info"
      rounded="lg"
    >
      <div class="d-flex flex-column ga-2">
        <div class="d-flex align-center justify-center ga-1 text-info font-weight-bold">
          <span>Your Best Servers</span>
        </div>
        <span class="text-caption text-medium-emphasis">
          {{ t('profile.channelPromo') }}
        </span>
        <v-btn
          href="https://t.me/ybs_today"
          target="_blank"
          rel="noopener noreferrer"
          color="info"
          variant="flat"
          rounded="lg"
          size="small"
          prepend-icon="ri-telegram-line"
          block
        >
          {{ t('profile.subscribeChannel') }}
        </v-btn>
        
        <div class="d-flex align-center justify-space-between w-100">
          <span class="text-caption text-medium-emphasis text-left">
            {{ t('profile.giveawayPromo') }}
          </span>
          <LeaderboardRulesTooltip :icon-size="16" />
        </div>
        <v-btn
          color="info"
          variant="tonal"
          rounded="lg"
          size="small"
          prepend-icon="ri-trophy-line"
          block
          @click="router.push({ name: ERouteNames.LEADERBOARD })"
        >
          {{ t('profile.viewLeaders') }}
        </v-btn>
      </div>
    </v-alert>

    <!-- Карточка профиля -->
    <v-card color="surface" rounded="xl" class="pa-4">
      <div class="d-flex flex-column ga-4">
        <!-- Email -->
        <div class="d-flex flex-column ga-1">
          <span class="text-caption">Email</span>
          <div class="d-flex align-center ga-2">
            <span
              class="text-body-1 flex-grow-1"
              :style="{ opacity: userStore.user?.email ? 1 : 0.5 }"
            >
              {{ userStore.user?.email || t('profile.notBound') }}
            </span>
            <VIconBtn
              v-if="userStore.user?.email && userStore.user?.telegramID"
              icon="ri-link-unlink"
              variant="text"
              color="warning"
              size="small"
              @click="showEmailUnlinkConfirm = true"
            />
            <VIconBtn
              v-if="userStore.user?.email"
              icon="ri-pencil-line"
              variant="text"
              color="secondary"
              size="small"
              @click="showEmailDialog = true"
            />
            <v-btn
              v-else
              variant="tonal"
              color="secondary"
              size="small"
              rounded="lg"
              @click="showEmailDialog = true"
            >
              {{ t('profile.bind') }}
            </v-btn>
          </div>
        </div>

        <v-divider />

        <!-- Имя -->
        <div class="d-flex flex-column ga-1">
          <span class="text-caption">{{ t('profile.name') }}</span>
          <div class="d-flex align-center ga-2">
            <div v-if="isEditingName" class="d-flex align-center ga-2 flex-grow-1">
              <v-text-field
                v-model="editNameValue"
                density="compact"
                hide-details
                variant="outlined"
                color="secondary"
                :loading="isSavingName"
                :disabled="isSavingName"
                class="flex-grow-1"
                @keyup.enter="saveName"
              />
              <VIconBtn
                icon="ri-check-line"
                variant="text"
                color="success"
                size="small"
                :loading="isSavingName"
                @click="saveName"
              />
            </div>
            <template v-else>
              <span
                class="text-body-1 flex-grow-1"
                :style="{ opacity: userStore.user?.firstName ? 1 : 0.5 }"
              >
                {{ userStore.user?.firstName || t('profile.notSpecified') }}
              </span>
              <VIconBtn
                icon="ri-pencil-line"
                variant="text"
                color="secondary"
                size="small"
                @click="startEditingName"
              />
            </template>
          </div>

          <v-checkbox
            :model-value="userStore.user?.isShowNamePublic"
            density="compact"
            hide-details
            color="success"
            :disabled="isSavingPrivacy"
            :label="t('profile.showInLeaderboard')"
            @update:model-value="handlePrivacyChange"
          />
        </div>

        <v-divider />

        <!-- ID пользователя -->
        <div class="d-flex flex-column ga-1">
          <span class="text-caption">ID</span>
          <div class="d-flex align-center ga-2">
            <span class="text-body-2 flex-grow-1">{{ userStore.user?.id || '—' }}</span>
            <VIconBtn
              v-if="userStore.user?.id"
              icon="ri-file-copy-line"
              variant="text"
              size="small"
              @click="copyToClipboard(userStore.user.id, 'ID')"
            />
          </div>
        </div>

        <v-divider />

        <!-- Telegram ID -->
        <div class="d-flex flex-column ga-1">
          <span class="text-caption">Telegram ID</span>
          <div class="d-flex align-center ga-2">
            <span v-if="userStore.user?.telegramID" class="text-body-2 flex-grow-1">{{ userStore.user?.telegramID }}</span>
            <span v-else class="text-body-2 flex-grow-1 opacity-50">{{ t('profile.notBound') }}</span>
            <VIconBtn
              v-if="userStore.user?.telegramID"
              icon="ri-file-copy-line"
              variant="text"
              size="small"
              @click="copyToClipboard(String(userStore.user.telegramID), 'Telegram ID')"
            />
            <VIconBtn
              v-if="userStore.user?.telegramID && userStore.user?.email"
              icon="ri-link-unlink"
              variant="text"
              color="warning"
              size="small"
              @click="showTelegramUnlinkConfirm = true"
            />
            <v-btn
              v-else-if="!userStore.user?.telegramID && authStore.isTelegramLoginAvailable && settingsStore.telegramBotId"
              variant="tonal"
              color="secondary"
              size="small"
              rounded="lg"
              :loading="isLoading"
              @click="handleTelegramOidcLink"
            >
              {{ t('profile.bind') }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Карточка тарифа и рефералов -->
    <v-card color="surface" rounded="xl" class="pa-4">
      <div class="d-flex flex-column ga-4">
        <!-- Статус тарифа -->
        <div class="d-flex flex-column ga-1">
          <span class="text-caption">{{ t('profile.tariff') }}</span>
          <div class="d-flex align-center ga-2">
            <span class="text-body-1 flex-grow-1">
              {{ userStore.user?.isTariffActive ? t('profile.until', { date: formattedTariffExpiry }) : '—' }}
            </span>
            <v-chip
              :color="userStore.user?.isTariffActive ? 'success' : 'error'"
              size="small"
              height="28"
              variant="tonal"
            >
              {{ userStore.user?.isTariffActive ? t('profile.active') : t('profile.inactive') }}
            </v-chip>
          </div>
        </div>

        <v-divider />

        <!-- Реферальная ссылка -->
        <div class="d-flex flex-column ga-1">
          <div class="d-flex align-center ga-2">
            <span class="text-caption">{{ t('profile.referralLink') }}</span>
            <ReferralBonusTooltip :icon-size="16" />
          </div>
          <div class="d-flex align-center ga-2">
            <span class="text-body-2 text-break flex-grow-1">{{ userStore.user?.referralLink || '—' }}</span>
            <share-referral-button icon-only />
          </div>
        </div>

        <v-divider />

        <!-- Количество рефералов -->
        <div class="d-flex flex-column ga-1">
          <span class="text-caption">{{ t('profile.invitedUsers') }}</span>
          <span class="text-body-2">{{ userStore.user?.referralCount ?? 0 }}</span>
        </div>

        <v-divider />

        <!-- Бонусные дни -->
        <div class="d-flex flex-column ga-1">
          <span class="text-caption">{{ t('profile.bonusDays') }}</span>
          <span class="text-body-2">{{ userStore.user?.referralBonusDaysCount ?? 0 }}</span>
        </div>
      </div>
    </v-card>

    <!-- Сохранённые способы оплаты -->
    <template v-if="payStore.savedPaymentMethods && payStore.savedPaymentMethods.length > 0">
      <div class="d-flex align-center justify-space-between w-100">
        <span class="text-subtitle-1 font-weight-medium">{{ t('profile.savedCards') }}</span>
      </div>
      <SavedCardItem
        v-for="method in payStore.savedPaymentMethods"
        :key="method.id"
        :payment-method="method"
      />
    </template>

    <!-- Кнопка выхода -->
    <v-btn
      v-if="isWebVersion"
      block
      color="error"
      variant="outlined"
      prepend-icon="ri-logout-box-line"
      class="flex-grow-0"
      @click="showLogoutConfirm = true"
    >
      {{ t('profile.logout') }}
    </v-btn>

    <!-- Кнопка удаления аккаунта -->
    <v-btn
      v-if="!userStore.user?.isTariffActive"
      block
      color="error"
      variant="text"
      prepend-icon="ri-delete-bin-line"
      class="flex-grow-0 mt-2"
      @click="showDeleteConfirm = true"
    >
      {{ t('profile.deleteAccount') }}
    </v-btn>

    <!-- Диалог подтверждения выхода -->
    <v-dialog v-model="showLogoutConfirm" max-width="360">
      <v-card color="surface" rounded="xl" class="pa-4">
        <div class="d-flex flex-column ga-4">
          <span class="text-h4">{{ t('profile.logoutConfirmTitle') }}</span>
          <span class="text-body-2">{{ t('profile.logoutConfirmDesc') }}</span>
          <div class="d-flex flex-column ga-2">
            <v-btn block color="error" @click="handleLogout">
              {{ t('profile.logout') }}
            </v-btn>
            <v-btn block variant="text" @click="showLogoutConfirm = false">
              {{ t('common.cancel') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <email-bind-dialog v-model="showEmailDialog" @email-bound="userStore.getUserData" />

    <!-- Диалог подтверждения отвязки Email -->
    <v-dialog v-model="showEmailUnlinkConfirm" max-width="360">
      <v-card color="surface" rounded="xl" class="pa-4">
        <div class="d-flex flex-column ga-4">
          <span class="text-h4">{{ t('profile.emailUnlinkTitle') }}</span>
          <span class="text-body-2">{{ t('profile.emailUnlinkDesc') }}</span>
          <div class="d-flex flex-column ga-2">
            <v-btn block color="warning" :loading="isUnlinkingEmail" @click="handleEmailUnlink">
              {{ t('common.unlink') }}
            </v-btn>
            <v-btn block variant="text" @click="showEmailUnlinkConfirm = false">
              {{ t('common.cancel') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Диалог подтверждения отвязки Telegram -->
    <v-dialog v-model="showTelegramUnlinkConfirm" max-width="360">
      <v-card color="surface" rounded="xl" class="pa-4">
        <div class="d-flex flex-column ga-4">
          <span class="text-h4">{{ t('profile.telegramUnlinkTitle') }}</span>
          <span class="text-body-2">{{ t('profile.telegramUnlinkDesc') }}</span>
          <div class="d-flex flex-column ga-2">
            <v-btn block color="warning" :loading="isUnlinkingTelegram" @click="handleTelegramUnlink">
              {{ t('common.unlink') }}
            </v-btn>
            <v-btn block variant="text" @click="showTelegramUnlinkConfirm = false">
              {{ t('common.cancel') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Диалог подтверждения удаления аккаунта -->
    <v-dialog v-model="showDeleteConfirm" max-width="360">
      <v-card color="surface" rounded="xl" class="pa-4">
        <div class="d-flex flex-column ga-4">
          <span class="text-h4">{{ t('profile.deleteConfirmTitle') }}</span>
          <span class="text-body-2">{{ t('profile.deleteConfirmDesc') }}</span>
          <div class="d-flex flex-column ga-2">
            <v-btn block color="error" :loading="isDeletingAccount" @click="handleDeleteAccount">
              {{ t('common.delete') }}
            </v-btn>
            <v-btn block variant="text" @click="showDeleteConfirm = false">
              {{ t('common.cancel') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>
